import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Printer, X, Pencil, Ban, HandCoins, MessageCircle, Star } from "lucide-react";
import { openWhatsApp, billReceiptMsg } from "../../lib/whatsapp";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls, PageHeader } from "./shared";
import { printInvoice } from "./printInvoice";

const emptyForm = { customer_name: "", phone: "", birthday: "", anniversary: "", date: "", discount: "", gst_enabled: false, gst_percent: 18, payment_method: "Cash", full_payment: true, amount_paid: "", due_date: "" };
const BILL_FILTERS = ["all", "paid", "pending", "voided"];
const todayStr = () => new Date().toISOString().split("T")[0];

const PREWRITTEN_REVIEWS = {
  good: [
    "Excellent service, very professional staff",
    "Loved my new look! Will definitely visit again",
    "Stylist understood exactly what I wanted",
    "Clean salon and very friendly team",
    "Great value for money",
  ],
  neutral: [
    "Service was okay, room for improvement",
    "Good service but had to wait long",
    "Average experience, expected better finishing",
  ],
  bad: [
    "Not satisfied with the service quality",
    "Waiting time was too long",
    "Staff behaviour needs improvement",
    "Final result was not what I asked for",
    "Salon hygiene needs attention",
  ],
};
const reviewBucket = (rating) => (rating >= 4 ? "good" : rating === 3 ? "neutral" : "bad");

export default function Billing() {
  const [bills, setBills] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [items, setItems] = useState([]);
  const [custom, setCustom] = useState({ name: "", price: "", qty: 1 });
  const [filter, setFilter] = useState("all");
  const [collecting, setCollecting] = useState(null);
  const [collectAmt, setCollectAmt] = useState("");
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState({ preset: "all", from: "", to: "" });
  const [splitPay, setSplitPay] = useState(false);
  const [payRows, setPayRows] = useState([{ uid: "pay-cash", method: "Cash", amount: "" }, { uid: "pay-upi", method: "UPI", amount: "" }]);
  const [clientQuery, setClientQuery] = useState("");
  const [svcQuery, setSvcQuery] = useState("");
  const [svcCat, setSvcCat] = useState("All");
  const [clientIdx, setClientIdx] = useState(0);
  const [svcIdx, setSvcIdx] = useState(0);
  const [prodQuery, setProdQuery] = useState("");
  const [prodIdx, setProdIdx] = useState(0);
  const [activeBillRow, setActiveBillRow] = useState(-1);
  const nameInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const [reviewing, setReviewing] = useState(null);
  const [review, setReview] = useState({ rating: 5, comments: [], note: "" });
  const [loyalty, setLoyalty] = useState(null);
  const [loySettings, setLoySettings] = useState({ enabled: true, earn_rate: 5, point_value: 1 });
  const [applyMembership, setApplyMembership] = useState(false);
  const [redeemPts, setRedeemPts] = useState("");

  const load = useCallback(() => api.get("/bills").then(({ data }) => setBills(data)).catch(() => {}), []);
  useEffect(() => {
    load();
    api.get("/services", { params: { all: true } }).then(({ data }) => setServices(data));
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/staff").then(({ data }) => setStaff(data)).catch(() => {});
    api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    api.get("/loyalty/settings").then(({ data }) => setLoySettings(data)).catch(() => {});
  }, [load]);

  useEffect(() => {
    const p = (form.phone || "").replace(/\D/g, "");
    if (p.length < 10) { setLoyalty(null); return; }
    api.get("/loyalty/customer", { params: { phone: form.phone } })
      .then(({ data }) => setLoyalty(data.found ? data : null))
      .catch(() => setLoyalty(null));
  }, [form.phone]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const equalize = (splits) => {
    const n = splits.length;
    if (!n) return [];
    const base = Math.floor(10000 / n) / 100;
    return splits.map((sp, i) => ({ ...sp, percent: i === n - 1 ? Math.round((100 - base * (n - 1)) * 100) / 100 : base }));
  };

  const pickClient = (c) => {
    setForm({ ...form, customer_name: c.name, phone: c.phone, birthday: c.birthday || "", anniversary: c.anniversary || "" });
    setClientQuery("");
  };

  const setWalkIn = () => {
    setForm({ ...form, customer_name: "Walk-in Client", phone: "", birthday: "", anniversary: "" });
    setClientQuery("");
  };

  const clientMatches = clientQuery.trim()
    ? customers.filter((c) => (c.name + " " + c.phone).toLowerCase().includes(clientQuery.trim().toLowerCase())).slice(0, 8)
    : [];

  const addNewClient = () => {
    const q = clientQuery.trim();
    if (!q) return;
    const isPhone = /^[\d\s+-]+$/.test(q);
    if (isPhone) {
      setForm({ ...form, phone: q.replace(/[^\d]/g, ""), customer_name: "" });
      setTimeout(() => nameInputRef.current?.focus(), 60);
    } else {
      setForm({ ...form, customer_name: q, phone: "" });
      setTimeout(() => phoneInputRef.current?.focus(), 60);
    }
    setClientQuery("");
    toast.info("New client — will be saved to Customers when the bill is created");
  };

  const clientKeyDown = (e) => {
    const n = clientMatches.length;
    if (e.key === "ArrowDown") { e.preventDefault(); setClientIdx((i) => Math.min(i + 1, Math.max(n - 1, 0))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setClientIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (n > 0) pickClient(clientMatches[Math.min(clientIdx, n - 1)]);
      else if (clientQuery.trim()) addNewClient();
    }
  };

  const svcMatches = services.filter((s) => {
    if (svcCat !== "All" && s.category !== svcCat) return false;
    const q = svcQuery.trim().toLowerCase();
    if (!q) return svcCat !== "All";
    return s.name.toLowerCase().includes(q) || String(s.price).includes(q);
  }).slice(0, 30);

  const addServiceItem = (s) => {
    setItems([...items, { uid: crypto.randomUUID(), name: s.name, price: s.price, qty: 1, staff_splits: [] }]);
    setSvcQuery("");
    setSvcIdx(0);
  };

  const listKeyNav = (e, count, idx, setIdx, onPick, elPrefix) => {
    if (!count) return;
    if (!["ArrowDown", "ArrowUp", "PageDown", "PageUp", "Enter"].includes(e.key)) return;
    e.preventDefault();
    let next = idx;
    if (e.key === "ArrowDown") next = Math.min(idx + 1, count - 1);
    if (e.key === "ArrowUp") next = Math.max(idx - 1, 0);
    if (e.key === "PageDown") next = Math.min(idx + 5, count - 1);
    if (e.key === "PageUp") next = Math.max(idx - 5, 0);
    if (e.key === "Enter") { onPick(Math.min(idx, count - 1)); return; }
    setIdx(next);
    setTimeout(() => document.getElementById(`${elPrefix}-${next}`)?.scrollIntoView({ block: "nearest" }), 0);
  };

  const prodMatches = products
    .filter((p) => (p.name + " " + (p.brand || "")).toLowerCase().includes(prodQuery.trim().toLowerCase()))
    .slice(0, 30);

  const addProductItem = (p) => {
    if (!p || (p.stock ?? 0) <= 0) { toast.error("Out of stock — restock before selling"); return; }
    setItems((prev) => [...prev, { uid: crypto.randomUUID(), name: p.name, price: p.selling_price, qty: 1, staff_splits: [], product_id: p.id }]);
    setProdQuery("");
    setProdIdx(0);
  };

  const addCustom = () => {
    if (!custom.name || !custom.price) { toast.error("Enter item name and price"); return; }
    setItems([...items, { uid: crypto.randomUUID(), name: custom.name, price: Number(custom.price), qty: Number(custom.qty) || 1, staff_splits: [] }]);
    setCustom({ name: "", price: "", qty: 1 });
  };

  const updateQty = (uid, qty) => setItems(items.map((it) => {
    if (it.uid !== uid) return it;
    const q = Math.max(1, Number(qty) || 1);
    const next = { ...it, qty: q };
    if (it.split_mode === "amt" && it.staff_splits.length > 0) next.staff_splits = absorbAmt(it.staff_splits, Math.round(it.price * q * 100) / 100);
    return next;
  }));
  const lineTotalOf = (it) => Math.round(it.price * it.qty * 100) / 100;
  const absorbAmt = (splits, lineTotal) => {
    if (!splits.length) return splits;
    const lastId = splits[splits.length - 1].staff_id;
    const sumOthers = splits.filter((sp) => sp.staff_id !== lastId).reduce((s, sp) => s + (Number(sp.amount) || 0), 0);
    return splits.map((sp) => (sp.staff_id === lastId ? { ...sp, amount: Math.max(0, Math.round((lineTotal - sumOthers) * 100) / 100) } : sp));
  };
  const addItemStaff = (uid, staff_id) =>
    setItems(items.map((it) => {
      if (it.uid !== uid || !staff_id || it.staff_splits.some((sp) => sp.staff_id === staff_id)) return it;
      if (it.split_mode === "amt") return { ...it, staff_splits: absorbAmt([...it.staff_splits, { staff_id, amount: 0 }], lineTotalOf(it)) };
      return { ...it, staff_splits: equalize([...it.staff_splits, { staff_id, percent: 0 }]) };
    }));
  const removeItemStaff = (uid, staff_id) =>
    setItems(items.map((it) => {
      if (it.uid !== uid) return it;
      const remaining = it.staff_splits.filter((sp) => sp.staff_id !== staff_id);
      return { ...it, staff_splits: it.split_mode === "amt" ? absorbAmt(remaining, lineTotalOf(it)) : equalize(remaining) };
    }));
  const setSplitPct = (uid, staff_id, val) =>
    setItems(items.map((it) => {
      if (it.uid !== uid) return it;
      const edited = it.staff_splits.map((sp) => (sp.staff_id === staff_id ? { ...sp, percent: val } : sp));
      const others = edited.filter((sp) => sp.staff_id !== staff_id);
      if (!others.length) return { ...it, staff_splits: edited };
      const lastId = others[others.length - 1].staff_id;
      const sumExceptLast = edited.filter((sp) => sp.staff_id !== lastId).reduce((s, sp) => s + (Number(sp.percent) || 0), 0);
      return { ...it, staff_splits: edited.map((sp) => (sp.staff_id === lastId ? { ...sp, percent: Math.max(0, Math.round((100 - sumExceptLast) * 100) / 100) } : sp)) };
    }));
  const setSplitAmt = (uid, staff_id, val) =>
    setItems(items.map((it) => {
      if (it.uid !== uid) return it;
      const lt = lineTotalOf(it);
      const edited = it.staff_splits.map((sp) => (sp.staff_id === staff_id ? { ...sp, amount: val } : sp));
      const others = edited.filter((sp) => sp.staff_id !== staff_id);
      if (!others.length) return { ...it, staff_splits: edited };
      const lastId = others[others.length - 1].staff_id;
      const sumExceptLast = edited.filter((sp) => sp.staff_id !== lastId).reduce((s, sp) => s + (Number(sp.amount) || 0), 0);
      return { ...it, staff_splits: edited.map((sp) => (sp.staff_id === lastId ? { ...sp, amount: Math.max(0, Math.round((lt - sumExceptLast) * 100) / 100) } : sp)) };
    }));
  const toggleSplitMode = (uid, mode) =>
    setItems(items.map((it) => {
      if (it.uid !== uid || (it.split_mode || "pct") === mode) return it;
      const lt = lineTotalOf(it);
      if (mode === "amt") {
        const conv = it.staff_splits.map((sp) => ({ staff_id: sp.staff_id, amount: Math.round(((Number(sp.percent) || 0) / 100) * lt * 100) / 100 }));
        return { ...it, split_mode: "amt", staff_splits: absorbAmt(conv, lt) };
      }
      const conv = it.staff_splits.map((sp) => ({ staff_id: sp.staff_id, percent: lt > 0 ? Math.round(((Number(sp.amount) || 0) / lt) * 10000) / 100 : 0 }));
      if (conv.length) {
        const lastId = conv[conv.length - 1].staff_id;
        const sumOthers = conv.filter((sp) => sp.staff_id !== lastId).reduce((s, sp) => s + sp.percent, 0);
        return { ...it, split_mode: "pct", staff_splits: conv.map((sp) => (sp.staff_id === lastId ? { ...sp, percent: Math.max(0, Math.round((100 - sumOthers) * 100) / 100) } : sp)) };
      }
      return { ...it, split_mode: "pct", staff_splits: conv };
    }));
  const removeItem = (uid) => setItems(items.filter((it) => it.uid !== uid));
  const staffName = (id) => staff.find((m) => m.id === id)?.name || "?";
  const splitSum = (it) => (it.split_mode === "amt"
    ? Math.round(it.staff_splits.reduce((s, sp) => s + (Number(sp.amount) || 0), 0) * 100) / 100
    : Math.round(it.staff_splits.reduce((s, sp) => s + (Number(sp.percent) || 0), 0) * 100) / 100);
  const splitBalanced = (it) => (it.split_mode === "amt" ? Math.abs(splitSum(it) - lineTotalOf(it)) < 0.05 : Math.abs(splitSum(it) - 100) < 0.01);

  const printBill = async (b) => {
    let loyaltyInfo = null;
    if (b.phone) {
      try {
        const { data } = await api.get("/loyalty/customer", { params: { phone: b.phone } });
        if (data.found) loyaltyInfo = data;
      } catch (e) {
        console.warn("Loyalty lookup failed — printing without points info:", e);
      }
    }
    printInvoice(b, loyaltyInfo);
  };

  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const discount = Math.min(Number(form.discount) || 0, subtotal);
  const membershipDiscount = applyMembership && loyalty?.membership?.active
    ? Math.round(Math.min(((subtotal - discount) * loyalty.membership.discount_percent) / 100, subtotal - discount) * 100) / 100
    : 0;
  const maxPts = (loyalty?.points || 0) + (editing?.points_redeemed || 0);
  const pointsRedeemed = Math.max(0, Math.min(Math.floor(Number(redeemPts) || 0), maxPts));
  const pointsValue = Math.round(Math.min(pointsRedeemed * (loySettings.point_value || 1), subtotal - discount - membershipDiscount) * 100) / 100;
  const gstAmount = form.gst_enabled ? ((subtotal - discount - membershipDiscount - pointsValue) * (Number(form.gst_percent) || 0)) / 100 : 0;
  const total = subtotal - discount - membershipDiscount - pointsValue + gstAmount;

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, date: todayStr() });
    setItems([]);
    setApplyMembership(false);
    setRedeemPts("");
    setSplitPay(false);
    setPayRows([{ method: "Cash", amount: "" }, { method: "UPI", amount: "" }]);
    setClientQuery("");
    setSvcQuery("");
    setOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      customer_name: b.customer_name, phone: b.phone || "", birthday: "", anniversary: "",
      date: b.date || "",
      discount: b.discount || "", gst_enabled: b.gst_enabled,
      gst_percent: b.gst_percent || 18, payment_method: b.payments?.length ? "Cash" : b.payment_method,
      full_payment: b.paid, amount_paid: b.paid ? "" : (b.amount_paid ?? ""), due_date: b.due_date || "",
    });
    setItems(b.items.map((it) => {
      const splits = it.staff_splits?.length
        ? it.staff_splits.map((sp) => ({ staff_id: sp.staff_id, percent: sp.percent }))
        : equalize((it.staff_ids || (it.staff_id ? [it.staff_id] : [])).map((sid) => ({ staff_id: sid, percent: 0 })));
      return { ...it, uid: crypto.randomUUID(), staff_splits: splits };
    }));
    setApplyMembership(!!b.membership_discount);
    setRedeemPts(b.points_redeemed ? String(b.points_redeemed) : "");
    setSplitPay(!!b.payments?.length);
    setPayRows(b.payments?.length ? b.payments.map((p) => ({ method: p.method, amount: String(p.amount) })) : [{ method: "Cash", amount: "" }, { method: "UPI", amount: "" }]);
    setClientQuery("");
    setSvcQuery("");
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.customer_name) { toast.error("Customer name required"); return; }
    if (items.length === 0) { toast.error("Add at least one item"); return; }
    const badSplit = items.find((it) => it.staff_splits.length > 0 && !splitBalanced(it));
    if (badSplit) {
      toast.error(badSplit.split_mode === "amt"
        ? `Staff ₹ split for "${badSplit.name}" must total ₹${lineTotalOf(badSplit).toLocaleString("en-IN")} (currently ₹${splitSum(badSplit).toLocaleString("en-IN")})`
        : `Staff work % for "${badSplit.name}" must total exactly 100% (currently ${splitSum(badSplit)}%)`);
      return;
    }
    const payments = splitPay ? payRows.filter((r) => Number(r.amount) > 0).map((r) => ({ method: r.method, amount: Number(r.amount) })) : [];
    if (splitPay && payments.length === 0) { toast.error("Enter at least one payment amount"); return; }
    const payload = {
      customer_name: form.customer_name,
      phone: form.phone,
      date: form.date || undefined,
      birthday: form.birthday,
      anniversary: form.anniversary,
      payment_method: form.payment_method,
      gst_enabled: form.gst_enabled,
      discount: discount,
      membership_discount: membershipDiscount,
      points_redeemed: pointsRedeemed,
      gst_percent: Number(form.gst_percent) || 0,
      items: items.map(({ uid, staff_id, staff_name: sn, staff_ids, staff_splits, split_mode, ...rest }) => {
        const lt = Math.round(rest.price * rest.qty * 100) / 100;
        let splits = (staff_splits || []).map((sp) => ({
          staff_id: sp.staff_id,
          percent: split_mode === "amt" ? (lt > 0 ? ((Number(sp.amount) || 0) / lt) * 100 : 0) : Number(sp.percent) || 0,
        }));
        if (split_mode === "amt" && splits.length > 0) {
          const sumOthers = splits.slice(0, -1).reduce((s, sp) => s + Math.round(sp.percent * 100) / 100, 0);
          splits = splits.map((sp, i2) => ({
            ...sp,
            percent: i2 === splits.length - 1 ? Math.round((100 - sumOthers) * 100) / 100 : Math.round(sp.percent * 100) / 100,
          }));
        }
        return { ...rest, staff_splits: splits };
      }),
      payments,
      paid: form.full_payment,
      amount_paid: form.full_payment ? null : Number(form.amount_paid) || 0,
      due_date: form.full_payment ? "" : form.due_date,
    };
    try {
      if (editing) {
        await api.put(`/bills/${editing.id}`, payload);
        toast.success(`Invoice ${editing.invoice_no} updated`);
      } else {
        const { data } = await api.post("/bills", payload);
        toast.success(`Invoice ${data.invoice_no} created`);
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      setItems([]);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const collectPayment = async (e) => {
    e.preventDefault();
    const amt = Number(collectAmt);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      const { data } = await api.post(`/bills/${collecting.id}/payment`, { amount: amt });
      toast.success(data.paid ? `${data.invoice_no} fully paid!` : `₹${amt.toLocaleString("en-IN")} collected — ₹${data.balance_due.toLocaleString("en-IN")} still due`);
      setCollecting(null);
      setCollectAmt("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openReview = (b) => {
    setReviewing(b);
    setReview(b.review ? { rating: b.review.rating, comments: b.review.comments || [], note: b.review.note || "" } : { rating: 5, comments: [], note: "" });
  };

  const toggleComment = (c) =>
    setReview((r) => ({ ...r, comments: r.comments.includes(c) ? r.comments.filter((x) => x !== c) : [...r.comments, c] }));

  const saveReview = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/bills/${reviewing.id}/review`, review);
      toast.success("Client feedback saved");
      setReviewing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleVoid = async (b) => {
    try {
      await api.patch(`/bills/${b.id}`, { voided: !b.voided });
      toast.success(b.voided ? `Invoice ${b.invoice_no} restored` : `Invoice ${b.invoice_no} voided`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/bills/${id}`); toast.success("Bill deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const activeBills = bills.filter((b) => !b.voided);
  const totalCollected = activeBills.reduce((s, b) => s + (b.amount_paid ?? (b.paid ? b.total : 0)), 0);
  const totalDues = activeBills.reduce((s, b) => s + (b.balance_due ?? (b.paid ? 0 : b.total)), 0);
  const isWalkIn = (b) => {
    const n = (b.customer_name || "").trim().toLowerCase();
    return !n || ["no entry", "walk-in", "walkin", "walk-in client", "-", "na", "n/a", "anonymous"].includes(n);
  };
  const rangeFor = (preset) => {
    const dstr = (x) => x.toISOString().split("T")[0];
    const now = new Date();
    if (preset === "today") return [dstr(now), dstr(now)];
    if (preset === "yesterday") { const y = new Date(now.getTime() - 864e5); return [dstr(y), dstr(y)]; }
    if (preset === "week") { const s = new Date(now); s.setDate(now.getDate() - ((now.getDay() + 6) % 7)); return [dstr(s), dstr(now)]; }
    if (preset === "month") return [dstr(new Date(now.getFullYear(), now.getMonth(), 1)), dstr(now)];
    if (preset === "custom") return [dateFilter.from || null, dateFilter.to || null];
    return [null, null];
  };
  const [rangeFrom, rangeTo] = rangeFor(dateFilter.preset);
  const q = search.trim().toLowerCase();
  const filteredBills = bills.filter((b) => {
    if (filter === "paid" && !(b.paid && !b.voided)) return false;
    if (filter === "pending" && !(!b.paid && !b.voided)) return false;
    if (filter === "voided" && !b.voided) return false;
    if (rangeFrom && b.date < rangeFrom) return false;
    if (rangeTo && b.date > rangeTo) return false;
    if (!q) return true;
    return (
      b.invoice_no.toLowerCase().includes(q) ||
      (b.customer_name || "").toLowerCase().includes(q) ||
      (b.phone || "").includes(q) ||
      (isWalkIn(b) && "walk-in anonymous guest".includes(q))
    );
  });

  useEffect(() => {
    const h = (e) => {
      if (open || collecting || reviewing) return;
      const tag = (e.target.tagName || "").toLowerCase();
      if (["input", "select", "textarea"].includes(tag)) return;
      if (e.key !== "PageDown" && e.key !== "PageUp") return;
      e.preventDefault();
      setActiveBillRow((i) => {
        const n = filteredBills.length;
        if (!n) return -1;
        const next = e.key === "PageDown" ? Math.min((i < 0 ? -1 : i) + 5, n - 1) : Math.max((i < 0 ? 0 : i) - 5, 0);
        document.getElementById(`bill-tr-${next}`)?.scrollIntoView({ block: "nearest" });
        return next;
      });
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, collecting, reviewing, filteredBills.length]);

  return (
    <div className="space-y-6" data-testid="billing-page">
      <PageHeader title="Billing" subtitle="Create client bills with GST option and printable invoices."
        action={
          <button onClick={openNew} data-testid="add-bill-btn"
            className="btn-lift bg-[#0A3F2F] text-white text-sm font-semibold px-4 py-2.5 rounded-full flex items-center gap-2 hover:bg-[#0d5540]">
            <Plus className="w-4 h-4" /> New Bill
          </button>
        }
      />
      <div className="flex flex-wrap items-center gap-3">
        <input data-testid="bill-search-input" className={`${inputCls} max-w-xs`} placeholder="🔍 Invoice no, client name, phone, walk-in…"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        {[["today", "Today"], ["yesterday", "Yesterday"], ["week", "This Week"], ["month", "This Month"], ["all", "All Time"]].map(([k, label]) => (
          <button key={k} onClick={() => setDateFilter({ ...dateFilter, preset: k })} data-testid={`date-preset-${k}`}
            className={`text-xs font-semibold px-3.5 py-2 rounded-full ${dateFilter.preset === k ? "bg-[#C5A059] text-[#1C1C1C]" : "bg-white border border-[#E5E7E5] text-[#5C6A63]"}`}>
            {label}
          </button>
        ))}
        <input data-testid="date-from-input" type="date" className={`${inputCls} w-36`} value={dateFilter.from} onChange={(e) => setDateFilter({ preset: "custom", from: e.target.value, to: dateFilter.to })} />
        <input data-testid="date-to-input" type="date" className={`${inputCls} w-36`} value={dateFilter.to} onChange={(e) => setDateFilter({ preset: "custom", from: dateFilter.from, to: e.target.value })} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2 flex-wrap">
          {BILL_FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`bill-filter-${f}`}
              className={`text-xs font-semibold px-4 py-2 rounded-full capitalize transition-colors ${
                filter === f ? "bg-[#0A3F2F] text-white" : "bg-white border border-[#E5E7E5] text-[#5C6A63] hover:border-[#0A3F2F]"
              }`}>
              {f === "pending" ? "Pending Payments" : f}
            </button>
          ))}
        </div>
        <div className="flex gap-3 text-sm" data-testid="billing-summary">
          <span className="bg-emerald-600/10 text-emerald-700 font-semibold px-4 py-2 rounded-full" data-testid="total-collected">
            Collected: ₹{totalCollected.toLocaleString("en-IN")}
          </span>
          <span className={`font-semibold px-4 py-2 rounded-full ${totalDues > 0 ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-500"}`} data-testid="total-dues">
            Pending Dues: ₹{totalDues.toLocaleString("en-IN")}
          </span>
        </div>
      </div>
      <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
              <th className="px-4 py-3 font-semibold">Invoice</th>
              <th className="px-4 py-3 font-semibold">Customer</th>
              <th className="px-4 py-3 font-semibold">Date</th>
              <th className="px-4 py-3 font-semibold">Items</th>
              <th className="px-4 py-3 font-semibold">GST</th>
              <th className="px-4 py-3 font-semibold">Total</th>
              <th className="px-4 py-3 font-semibold">Payment</th>
              <th className="px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="bills-table-body">
            {filteredBills.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-[#5C6A63]" data-testid="no-bills">No bills found.</td></tr>
            )}
            {filteredBills.map((b, bIdx) => (
              <tr key={b.id} id={`bill-tr-${bIdx}`} className={`border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8] ${b.voided ? "opacity-50" : ""} ${activeBillRow === bIdx ? "bg-[#C5A059]/10" : ""}`} data-testid={`bill-row-${b.id}`}>
                <td className="px-4 py-3 font-semibold text-[#0A3F2F]">
                  {b.invoice_no}
                  {b.voided && <span className="ml-2 text-[10px] font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full" data-testid={`void-badge-${b.id}`}>VOID</span>}
                  {b.review && (
                    <span data-testid={`review-badge-${b.id}`}
                      className={`ml-2 inline-flex items-center gap-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full ${b.review.rating >= 4 ? "bg-emerald-600/10 text-emerald-700" : b.review.rating === 3 ? "bg-[#C5A059]/20 text-[#8a6d33]" : "bg-red-100 text-red-600"}`}>
                      <Star className="w-2.5 h-2.5 fill-current" />{b.review.rating}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {isWalkIn(b) ? (
                    <span data-testid={`walkin-badge-${b.id}`} className="text-[11px] font-bold bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full">Walk-in Client</span>
                  ) : (
                    <p className="font-semibold text-[#1C1C1C]">{b.customer_name}</p>
                  )}
                  <p className="text-xs text-[#5C6A63]">{b.phone || "N/A"}</p>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">{b.date}</td>
                <td className="px-4 py-3 text-[#5C6A63]">{b.items.length} item{b.items.length > 1 ? "s" : ""}</td>
                <td className="px-4 py-3 text-xs">{b.gst_enabled ? `${b.gst_percent}% (₹${b.gst_amount.toLocaleString("en-IN")})` : "Without GST"}</td>
                <td className="px-4 py-3 font-bold text-[#0A3F2F]">₹{b.total.toLocaleString("en-IN")}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#5C6A63]">{b.payment_method}</span>
                    {b.paid ? (
                      <span data-testid={`paid-badge-${b.id}`} className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-600/10 text-emerald-700">Paid</span>
                    ) : (
                      <div>
                        <span data-testid={`due-badge-${b.id}`}
                          className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${b.due_date && b.due_date < todayStr() ? "bg-red-100 text-red-600" : "bg-[#C5A059]/20 text-[#8a6d33]"}`}>
                          Due ₹{(b.balance_due ?? b.total).toLocaleString("en-IN")}
                        </span>
                        {b.due_date && (
                          <p className={`text-[10px] mt-1 ${b.due_date < todayStr() ? "text-red-600 font-bold" : "text-[#5C6A63]"}`} data-testid={`due-date-${b.id}`}>
                            {b.due_date < todayStr() ? "Overdue since " : "Remind on "}{b.due_date}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-3">
                    {!b.voided && !b.paid && (
                      <button onClick={() => { setCollecting(b); setCollectAmt(String(b.balance_due ?? b.total)); }} data-testid={`collect-bill-${b.id}`} title="Collect payment"
                        className="text-[#C5A059] hover:text-[#0A3F2F] transition-colors"><HandCoins className="w-4 h-4" /></button>
                    )}
                    {!b.voided && (
                      <button onClick={() => openEdit(b)} data-testid={`edit-bill-${b.id}`} title="Edit bill"
                        className="text-[#3D4A44] hover:text-[#0A3F2F] transition-colors"><Pencil className="w-4 h-4" /></button>
                    )}
                    {!b.voided && (
                      <button onClick={() => openReview(b)} data-testid={`review-bill-${b.id}`} title="Client feedback"
                        className={`transition-colors ${b.review ? "text-[#C5A059]" : "text-[#3D4A44]"} hover:text-[#0A3F2F]`}><Star className={`w-4 h-4 ${b.review ? "fill-current" : ""}`} /></button>
                    )}
                    {!b.voided && b.phone && (
                      <button onClick={() => openWhatsApp(b.phone, billReceiptMsg(b))} data-testid={`whatsapp-bill-${b.id}`} title="Send receipt on WhatsApp"
                        className="text-emerald-600 hover:text-emerald-700 transition-colors"><MessageCircle className="w-4 h-4" /></button>
                    )}
                    <button onClick={() => printBill(b)} data-testid={`print-bill-${b.id}`} title="Print invoice"
                      className="text-[#3D4A44] hover:text-[#0A3F2F] transition-colors"><Printer className="w-4 h-4" /></button>
                    <button onClick={() => toggleVoid(b)} data-testid={`void-bill-${b.id}`} title={b.voided ? "Restore bill" : "Void bill"}
                      className={`transition-colors ${b.voided ? "text-red-500 hover:text-emerald-600" : "text-[#3D4A44] hover:text-red-600"}`}><Ban className="w-4 h-4" /></button>
                    <button onClick={() => remove(b.id)} data-testid={`delete-bill-${b.id}`} title="Delete bill"
                      className="text-[#3D4A44] hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Edit Bill — ${editing.invoice_no}` : "New Bill"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="bill-form">
            <div className="relative">
              <div className="flex gap-2">
                <input data-testid="bill-client-search-input" className={inputCls} placeholder="🔍 Find existing client by name or phone…"
                  value={clientQuery} onChange={(e) => { setClientQuery(e.target.value); setClientIdx(0); }} onKeyDown={clientKeyDown} />
                <button type="button" onClick={setWalkIn} data-testid="bill-walkin-btn"
                  className="whitespace-nowrap bg-[#C5A059]/20 text-[#8a6d33] text-xs font-bold px-4 rounded-md hover:bg-[#C5A059]/40">
                  Walk-in
                </button>
              </div>
              {clientQuery.trim() && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7E5] rounded-md shadow-lg max-h-48 overflow-y-auto" data-testid="client-suggestions">
                  {clientMatches.map((c, ci) => (
                    <button key={c.id} type="button" onClick={() => pickClient(c)} data-testid={`client-suggestion-${c.id}`}
                      className={`w-full text-left px-4 py-2.5 text-sm border-b border-[#E5E7E5] last:border-0 ${ci === clientIdx ? "bg-[#C5A059]/15" : "hover:bg-[#FAFAF8]"}`}>
                      <span className="font-semibold">{c.name}</span> <span className="text-[#5C6A63]">· {c.phone}</span>
                      {c.tag && <span className="ml-2 text-[10px] font-bold text-[#8a6d33]">{c.tag}</span>}
                    </button>
                  ))}
                  {clientMatches.length === 0 && (
                    <button type="button" onClick={addNewClient} data-testid="add-new-client-btn"
                      className="w-full text-left px-4 py-3 text-sm font-bold text-[#0A3F2F] bg-[#0A3F2F]/5 hover:bg-[#0A3F2F]/10 flex items-center gap-2">
                      <Plus className="w-4 h-4" /> Add New Client: “{clientQuery.trim()}”
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="bill-customer-input" ref={nameInputRef} className={inputCls} placeholder="Customer name *" value={form.customer_name} onChange={set("customer_name")} />
              <input data-testid="bill-phone-input" ref={phoneInputRef} className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Client birthday</label>
                <input data-testid="bill-birthday-input" type="date" className={inputCls} value={form.birthday} onChange={set("birthday")} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Client anniversary</label>
                <input data-testid="bill-anniversary-input" type="date" className={inputCls} value={form.anniversary} onChange={set("anniversary")} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Bill date</label>
                <input data-testid="bill-date-input" type="date" className={inputCls} value={form.date} onChange={set("date")} />
              </div>
            </div>

            {loyalty && (
              <div className="bg-[#0A3F2F]/5 border border-[#0A3F2F]/20 rounded-md p-4 space-y-3" data-testid="bill-loyalty-panel">
                <p className="text-xs font-bold uppercase tracking-wider text-[#0A3F2F]">Loyalty — {loyalty.name}</p>
                <div className="flex flex-wrap items-center gap-3">
                  {loyalty.membership ? (
                    loyalty.membership.active ? (
                      <label className="flex items-center gap-2 text-sm font-medium" data-testid="bill-membership-toggle">
                        <input type="checkbox" checked={applyMembership} onChange={(e) => setApplyMembership(e.target.checked)} data-testid="bill-membership-checkbox" />
                        <span className="bg-[#C5A059]/20 text-[#8a6d33] text-xs font-bold px-2.5 py-1 rounded-full">{loyalty.membership.plan_name} · {loyalty.membership.discount_percent}% off</span>
                        <span className="text-[11px] text-[#5C6A63]">valid till {loyalty.membership.expiry_date}</span>
                      </label>
                    ) : (
                      <span className="text-xs text-red-600 font-semibold" data-testid="bill-membership-expired">{loyalty.membership.plan_name} expired on {loyalty.membership.expiry_date}</span>
                    )
                  ) : (
                    <span className="text-xs text-[#5C6A63]">No membership</span>
                  )}
                  <span className="text-xs font-semibold text-[#0A3F2F]" data-testid="bill-points-balance">★ {loyalty.points || 0} points (₹{((loyalty.points || 0) * (loySettings.point_value || 1)).toLocaleString("en-IN")})</span>
                  {maxPts > 0 && (
                    <div className="flex items-center gap-1.5 text-xs">
                      Redeem
                      <input type="number" min="0" max={maxPts} data-testid="bill-redeem-points-input"
                        className="w-20 border border-[#E5E7E5] rounded-md px-2 py-1" placeholder="0"
                        value={redeemPts} onChange={(e) => setRedeemPts(e.target.value)} />
                      pts
                    </div>
                  )}
                </div>
                {loyalty.packages?.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="bill-packages-row">
                    {loyalty.packages.map((p) => {
                      const left = p.total_sessions - (p.sessions_used || 0) - items.filter((it) => it.package_id === p.id).reduce((s, it) => s + it.qty, 0);
                      return (
                        <button key={p.id} type="button" disabled={left < 1} data-testid={`bill-use-package-${p.id}`}
                          onClick={() => setItems([...items, { uid: crypto.randomUUID(), name: `${p.name} (package session)`, price: 0, qty: 1, staff_splits: [], package_id: p.id }])}
                          className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#0A3F2F]/30 text-[#0A3F2F] hover:bg-[#0A3F2F] hover:text-white disabled:opacity-40 disabled:pointer-events-none transition-colors">
                          {p.name} · {left} session{left === 1 ? "" : "s"} left — use 1
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="border border-[#E5E7E5] rounded-md p-4 space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider text-[#5C6A63]">Items</p>
              <div className="space-y-2">
                <div className="flex gap-1.5 flex-wrap">
                  {["All", "Hair", "Skin", "Makeup", "Packages", "Academy"].map((c) => (
                    <button key={c} type="button" onClick={() => setSvcCat(c)} data-testid={`svc-cat-${c.toLowerCase()}`}
                      className={`text-[11px] font-bold px-3 py-1 rounded-full ${svcCat === c ? "bg-[#0A3F2F] text-white" : "bg-white border border-[#E5E7E5] text-[#5C6A63]"}`}>
                      {c}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <input data-testid="bill-service-search-input" className={inputCls} placeholder="🔍 Search service by name or price…"
                      value={svcQuery} onChange={(e) => { setSvcQuery(e.target.value); setSvcIdx(0); }}
                      onKeyDown={(e) => listKeyNav(e, svcMatches.length, svcIdx, setSvcIdx, (i) => addServiceItem(svcMatches[i]), "svc-opt")} />
                    {svcMatches.length > 0 && (svcQuery || svcCat !== "All") && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7E5] rounded-md shadow-lg max-h-44 overflow-y-auto" data-testid="service-suggestions">
                        {svcMatches.map((s, si) => (
                          <button key={s.id} id={`svc-opt-${si}`} type="button" onClick={() => addServiceItem(s)} data-testid={`service-suggestion-${s.id}`}
                            className={`w-full text-left px-3 py-2 text-sm border-b border-[#E5E7E5] last:border-0 ${si === svcIdx ? "bg-[#C5A059]/15" : "hover:bg-[#FAFAF8]"}`}>
                            {s.name} <span className="text-[#5C6A63]">— ₹{s.price}</span> <span className="text-[10px] text-[#8a6d33]">{s.category}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <input data-testid="bill-product-search-input" className={inputCls} placeholder="🛍️ + Sell a product…"
                      value={prodQuery} onChange={(e) => { setProdQuery(e.target.value); setProdIdx(0); }}
                      onKeyDown={(e) => listKeyNav(e, prodMatches.length, prodIdx, setProdIdx, (i) => addProductItem(prodMatches[i]), "prod-opt")} />
                    {prodQuery.trim() && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7E5] rounded-md shadow-lg max-h-44 overflow-y-auto" data-testid="product-suggestions">
                        {prodMatches.length === 0 && <p className="px-3 py-2 text-xs text-[#5C6A63]">No matching products.</p>}
                        {prodMatches.map((p, pi) => (
                          <button key={p.id} id={`prod-opt-${pi}`} type="button" onClick={() => addProductItem(p)} data-testid={`product-suggestion-${p.id}`}
                            disabled={(p.stock ?? 0) <= 0}
                            className={`w-full text-left px-3 py-2 text-sm border-b border-[#E5E7E5] last:border-0 flex items-center justify-between gap-2 ${(p.stock ?? 0) <= 0 ? "opacity-40 cursor-not-allowed" : pi === prodIdx ? "bg-[#C5A059]/15" : "hover:bg-[#FAFAF8]"}`}>
                            <span>{p.name} <span className="text-[#5C6A63]">— ₹{p.selling_price}</span></span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${(p.stock ?? 0) <= 0 ? "bg-red-100 text-red-600" : (p.stock ?? 0) <= (p.low_stock_level ?? 2) ? "bg-[#C5A059]/20 text-[#8a6d33]" : "bg-emerald-600/10 text-emerald-700"}`}>
                              {(p.stock ?? 0) <= 0 ? "OUT" : `${p.stock} left`}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-[1fr_100px_70px_auto] gap-2 items-center">
                <input data-testid="bill-custom-name-input" className={inputCls} placeholder="Custom item / product" value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} />
                <input data-testid="bill-custom-price-input" type="number" className={inputCls} placeholder="Price" value={custom.price} onChange={(e) => setCustom({ ...custom, price: e.target.value })} />
                <input data-testid="bill-custom-qty-input" type="number" min="1" className={inputCls} value={custom.qty} onChange={(e) => setCustom({ ...custom, qty: e.target.value })} />
                <button type="button" onClick={addCustom} data-testid="bill-add-custom-btn"
                  className="bg-[#C5A059] text-[#1C1C1C] rounded-md px-3 py-2.5 text-sm font-semibold hover:bg-[#d4b06c]">Add</button>
              </div>
              {items.length > 0 && (
                <div className="space-y-2" data-testid="bill-items-list">
                  {items.map((it, i) => (
                    <div key={it.uid} className="bg-[#FAFAF8] rounded-md px-3 py-2 space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="flex-1 font-medium">{it.name}{it.product_id ? <span className="ml-1 text-[9px] uppercase font-bold text-[#8a6d33]">product</span> : null}{it.package_id ? <span className="ml-1 text-[9px] uppercase font-bold text-[#0A3F2F]">package</span> : null}</span>
                        <span className="text-[#5C6A63]">₹{it.price}</span>
                        <span className="text-[#5C6A63]">×</span>
                        <input type="number" min="1" value={it.qty} onChange={(e) => updateQty(it.uid, e.target.value)} data-testid={`bill-item-qty-${i}`}
                          className="w-14 border border-[#E5E7E5] rounded-md px-2 py-1 text-sm text-center" />
                        <span className="font-semibold w-20 text-right">₹{(it.price * it.qty).toLocaleString("en-IN")}</span>
                        <button type="button" onClick={() => removeItem(it.uid)} data-testid={`bill-remove-item-${i}`} className="text-gray-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap" data-testid={`bill-item-staff-row-${i}`}>
                        {it.staff_splits.length > 0 && (
                          <span className="inline-flex rounded-full border border-[#E5E7E5] overflow-hidden" data-testid={`split-mode-toggle-${i}`}>
                            <button type="button" onClick={() => toggleSplitMode(it.uid, "pct")} data-testid={`split-mode-pct-${i}`} title="Split by percentage"
                              className={`px-2.5 py-1 text-[10px] font-bold ${(it.split_mode || "pct") === "pct" ? "bg-[#0A3F2F] text-white" : "bg-white text-[#5C6A63] hover:bg-[#FAFAF8]"}`}>%</button>
                            <button type="button" onClick={() => toggleSplitMode(it.uid, "amt")} data-testid={`split-mode-amt-${i}`} title="Split by fixed amount"
                              className={`px-2.5 py-1 text-[10px] font-bold ${it.split_mode === "amt" ? "bg-[#0A3F2F] text-white" : "bg-white text-[#5C6A63] hover:bg-[#FAFAF8]"}`}>₹</button>
                          </span>
                        )}
                        {it.staff_splits.map((sp) => (
                          <span key={sp.staff_id} className="inline-flex items-center gap-1.5 bg-[#0A3F2F] text-white text-[11px] font-semibold pl-2.5 pr-1.5 py-1 rounded-full" data-testid={`staff-chip-${i}-${sp.staff_id}`}>
                            {staffName(sp.staff_id)}
                            {it.split_mode === "amt" ? (
                              <>
                                <span className="text-[#C5A059]">₹</span>
                                <input type="number" min="0" step="any" value={sp.amount} data-testid={`split-amt-${i}-${sp.staff_id}`}
                                  onChange={(e) => setSplitAmt(it.uid, sp.staff_id, e.target.value)}
                                  className="w-16 bg-white/15 rounded px-1 py-0.5 text-center text-[11px] text-[#C5A059] font-bold" />
                              </>
                            ) : (
                              <>
                                <input type="number" min="0" max="100" step="any" value={sp.percent} data-testid={`split-pct-${i}-${sp.staff_id}`}
                                  onChange={(e) => setSplitPct(it.uid, sp.staff_id, e.target.value)}
                                  className="w-12 bg-white/15 rounded px-1 py-0.5 text-center text-[11px] text-[#C5A059] font-bold" />
                                <span className="text-[#C5A059]">%</span>
                              </>
                            )}
                            <button type="button" onClick={() => removeItemStaff(it.uid, sp.staff_id)} className="hover:text-red-300"><X className="w-3 h-3" /></button>
                          </span>
                        ))}
                        <select data-testid={`bill-item-staff-${i}`} value="" onChange={(e) => addItemStaff(it.uid, e.target.value)}
                          className="border border-[#E5E7E5] rounded-md px-2 py-1 text-xs bg-white">
                          <option value="">+ Add staff…</option>
                          {staff.filter((m) => !it.staff_splits.some((sp) => sp.staff_id === m.id)).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                        {it.staff_splits.length > 0 && (
                          splitBalanced(it) ? (
                            <span data-testid={`split-sum-${i}`} className="text-[10px] font-bold text-emerald-700 bg-emerald-600/10 px-2.5 py-1 rounded-full">
                              {it.split_mode === "amt"
                                ? `₹${splitSum(it).toLocaleString("en-IN")} / ₹${lineTotalOf(it).toLocaleString("en-IN")} ✓`
                                : "100% ✓"}
                            </span>
                          ) : (
                            <span data-testid={`split-sum-${i}`} className="text-[10px] font-bold text-[#8a6d33] bg-[#C5A059]/25 px-2.5 py-1 rounded-full">
                              {it.split_mode === "amt"
                                ? (lineTotalOf(it) - splitSum(it) > 0
                                  ? `₹${Math.round((lineTotalOf(it) - splitSum(it)) * 100) / 100} remaining`
                                  : `₹${Math.round((splitSum(it) - lineTotalOf(it)) * 100) / 100} over`)
                                : (100 - splitSum(it) > 0
                                  ? `${Math.round((100 - splitSum(it)) * 100) / 100}% short`
                                  : `${Math.round((splitSum(it) - 100) * 100) / 100}% over`)}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <input data-testid="bill-discount-input" type="number" min="0" className={inputCls} placeholder="Discount (₹)" value={form.discount} onChange={set("discount")} />
              <select data-testid="bill-payment-select" className={inputCls} value={form.payment_method} onChange={set("payment_method")}>
                <option>Cash</option><option>UPI</option><option>Card</option>
              </select>
            </div>
            <div className="flex items-center gap-6 flex-wrap">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" data-testid="bill-gst-checkbox" checked={form.gst_enabled} onChange={(e) => setForm({ ...form, gst_enabled: e.target.checked })} />
                With GST
              </label>
              {form.gst_enabled && (
                <div className="flex items-center gap-2 text-sm">
                  GST % <input data-testid="bill-gst-percent-input" type="number" min="0" className="w-20 border border-[#E5E7E5] rounded-md px-2 py-1.5" value={form.gst_percent} onChange={set("gst_percent")} />
                </div>
              )}
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" data-testid="bill-split-pay-checkbox" checked={splitPay} onChange={(e) => setSplitPay(e.target.checked)} />
                Split payment (multiple modes)
              </label>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" data-testid="bill-full-payment-checkbox" checked={form.full_payment} onChange={(e) => setForm({ ...form, full_payment: e.target.checked })} disabled={splitPay} />
                Full payment received
              </label>
            </div>
            {splitPay && (
              <div className="space-y-2 bg-[#FAFAF8] border border-[#E5E7E5] rounded-md p-4" data-testid="split-pay-section">
                {payRows.map((r, i) => (
                  <div key={r.uid} className="flex gap-2">
                    <select data-testid={`pay-row-method-${i}`} className={inputCls} value={r.method} onChange={(e) => setPayRows(payRows.map((x) => (x.uid === r.uid ? { ...x, method: e.target.value } : x)))}>
                      <option>Cash</option><option>UPI</option><option>Card</option><option>Bank Transfer</option>
                    </select>
                    <input data-testid={`pay-row-amount-${i}`} type="number" min="0" step="any" className={inputCls} placeholder="Amount (₹)"
                      value={r.amount} onChange={(e) => setPayRows(payRows.map((x) => (x.uid === r.uid ? { ...x, amount: e.target.value } : x)))} />
                  </div>
                ))}
                <div className="flex justify-between items-center">
                  <button type="button" data-testid="add-pay-row-btn" onClick={() => setPayRows([...payRows, { uid: crypto.randomUUID(), method: "Card", amount: "" }])}
                    className="text-xs font-bold text-[#0A3F2F] underline underline-offset-4">+ Add payment mode</button>
                  <span className="text-xs font-bold text-[#8a6d33]" data-testid="split-pay-balance">
                    Received: ₹{payRows.reduce((s, r) => s + (Number(r.amount) || 0), 0).toLocaleString("en-IN")} · Balance: ₹{Math.max(0, total - payRows.reduce((s, r) => s + (Number(r.amount) || 0), 0)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}
            {!form.full_payment && (
              <div className="grid grid-cols-2 gap-3 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4" data-testid="partial-payment-section">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Amount received now (₹)</label>
                  <input data-testid="bill-amount-paid-input" type="number" min="0" className={inputCls} placeholder="e.g. 600" value={form.amount_paid} onChange={set("amount_paid")} />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Payment reminder date</label>
                  <input data-testid="bill-due-date-input" type="date" className={inputCls} value={form.due_date} onChange={set("due_date")} />
                </div>
                <p className="col-span-2 text-xs text-[#8a6d33] font-semibold">
                  Balance due: ₹{Math.max(0, total - (Number(form.amount_paid) || 0)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                </p>
              </div>
            )}

            <div className="bg-[#0A3F2F] text-white rounded-md p-4 text-sm space-y-1" data-testid="bill-totals">
              <div className="flex justify-between"><span className="text-white/70">Subtotal</span><span>₹{subtotal.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between"><span className="text-white/70">Discount</span><span>− ₹{discount.toLocaleString("en-IN")}</span></div>
              {membershipDiscount > 0 && <div className="flex justify-between"><span className="text-white/70">Membership ({loyalty?.membership?.discount_percent}%)</span><span data-testid="bill-membership-discount">− ₹{membershipDiscount.toLocaleString("en-IN")}</span></div>}
              {pointsValue > 0 && <div className="flex justify-between"><span className="text-white/70">Reward Points ({pointsRedeemed} pts)</span><span data-testid="bill-points-value">− ₹{pointsValue.toLocaleString("en-IN")}</span></div>}
              {form.gst_enabled && <div className="flex justify-between"><span className="text-white/70">GST ({form.gst_percent}%)</span><span>+ ₹{gstAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>}
              <div className="flex justify-between font-bold text-base pt-2 border-t border-white/20"><span>Total</span><span className="text-[#C5A059]" data-testid="bill-total-amount">₹{total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
              {loySettings.enabled && form.phone && total > 0 && (
                <p className="text-[11px] text-white/60 pt-1" data-testid="bill-points-earn-preview">Client earns {Math.floor((total * (loySettings.earn_rate || 0)) / 100)} reward points on this bill</p>
              )}
            </div>

            <button type="submit" data-testid="bill-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-3 rounded-full text-sm hover:bg-[#0d5540]">
              {editing ? "Save Changes" : "Create Bill"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!collecting} onOpenChange={(v) => !v && setCollecting(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Collect Payment — {collecting?.invoice_no}</DialogTitle></DialogHeader>
          <form onSubmit={collectPayment} className="space-y-4" data-testid="collect-form">
            <div className="bg-[#FAFAF8] rounded-md p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-[#5C6A63]">Customer</span><span className="font-semibold">{collecting?.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-[#5C6A63]">Bill total</span><span>₹{collecting?.total?.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between"><span className="text-[#5C6A63]">Already received</span><span>₹{(collecting?.amount_paid || 0).toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between font-bold text-red-600"><span>Balance due</span><span data-testid="collect-balance">₹{(collecting?.balance_due ?? 0).toLocaleString("en-IN")}</span></div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Amount collecting now (₹)</label>
              <input data-testid="collect-amount-input" type="number" min="1" className={inputCls} value={collectAmt} onChange={(e) => setCollectAmt(e.target.value)} />
            </div>
            <button type="submit" data-testid="collect-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              Record Payment
            </button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!reviewing} onOpenChange={(v) => !v && setReviewing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Client Feedback — {reviewing?.invoice_no} ({reviewing?.customer_name})</DialogTitle></DialogHeader>
          <form onSubmit={saveReview} className="space-y-4" data-testid="review-form">
            <div className="flex items-center gap-2 justify-center py-2" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`review-star-${n}`}
                  onClick={() => setReview({ ...review, rating: n, comments: [] })}
                  className={`transition-transform hover:scale-110 ${n <= review.rating ? "text-[#C5A059]" : "text-gray-300"}`}>
                  <Star className={`w-8 h-8 ${n <= review.rating ? "fill-current" : ""}`} />
                </button>
              ))}
            </div>
            <p className="text-center text-sm font-semibold text-[#5C6A63]">
              {review.rating >= 4 ? "Great! What did the client love?" : review.rating === 3 ? "Okay — what could be better?" : "Sorry to hear — what went wrong?"}
            </p>
            <div className="flex flex-wrap gap-2" data-testid="review-comments">
              {PREWRITTEN_REVIEWS[reviewBucket(review.rating)].map((c) => (
                <button key={c} type="button" onClick={() => toggleComment(c)}
                  data-testid={`review-comment-chip-${PREWRITTEN_REVIEWS[reviewBucket(review.rating)].indexOf(c)}`}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                    review.comments.includes(c) ? "bg-[#0A3F2F] text-white border-[#0A3F2F]" : "bg-white text-[#5C6A63] border-[#E5E7E5] hover:border-[#0A3F2F]"
                  }`}>
                  {c}
                </button>
              ))}
            </div>
            <textarea data-testid="review-note-input" className={inputCls} rows={2} placeholder="Additional feedback (optional)"
              value={review.note} onChange={(e) => setReview({ ...review, note: e.target.value })} />
            <button type="submit" data-testid="review-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              Save Feedback
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
